import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { GlassCard, Spinner, StatusBadge } from "@/components/shared";
import { CheckCircle2, Loader2, XCircle, ArrowRight, PartyPopper } from "lucide-react";

const STEP_ORDER = ["account", "compute", "storage", "pbs", "secure", "datastore", "credentials", "connectivity", "ready"];

export default function PaymentResult() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const canceled = window.location.pathname.includes("cancel");
  const [phase, setPhase] = useState(canceled ? "canceled" : "verifying");
  const [serverId, setServerId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [note, setNote] = useState(null);
  const poll = useRef(null);

  useEffect(() => {
    if (canceled || !sessionId) return;
    let stopped = false;
    const check = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid" && data.server_id) {
          setServerId(data.server_id);
          setPhase("provisioning");
          return true;
        }
      } catch {}
      return false;
    };
    const loop = async () => {
      if (stopped) return;
      const done = await check();
      if (!done) setTimeout(loop, 2000);
    };
    loop();
    return () => { stopped = true; };
  }, [sessionId, canceled]);

  useEffect(() => {
    if (phase !== "provisioning" || !serverId) return;
    const load = async () => {
      const { data } = await api.get(`/servers/${serverId}/provisioning`);
      setSteps(data.steps);
      if (data.status === "completed") { setPhase("done"); clearInterval(poll.current); }
      else if (data.status === "failed") { setPhase("failed"); clearInterval(poll.current); }
      else if (data.status === "action_required") { setNote(data.current_step || "This is taking longer than usual — your server is running and will finish shortly."); }
      else { setNote(null); }
    };
    load();
    poll.current = setInterval(load, 1500);
    return () => clearInterval(poll.current);
  }, [phase, serverId]);

  if (canceled) {
    return (
      <Center>
        <XCircle className="h-12 w-12 text-amber-400" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-50">Checkout canceled</h1>
        <p className="mt-2 text-slate-400">No charge was made. You can restart the deployment any time.</p>
        <button onClick={() => nav("/app/deploy")} className="mt-6 rounded-lg bg-cyan-400 px-6 py-2.5 font-semibold text-[#06080D]">Back to deploy</button>
      </Center>
    );
  }

  if (phase === "verifying") {
    return <Center><Spinner className="h-10 w-10" /><h1 className="mt-4 font-display text-xl font-semibold text-slate-100">Confirming your payment…</h1><p className="mt-1 text-sm text-slate-500">This takes a few seconds.</p></Center>;
  }

  const stepMap = Object.fromEntries(steps.map((s) => [s.key, s]));
  if (phase === "failed") {
    return (
      <Center>
        <XCircle className="h-12 w-12 text-rose-400" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-50">Provisioning couldn't complete</h1>
        <p className="mt-2 max-w-md text-center text-slate-400">We hit a problem setting up your server. You weren't charged for unusable infrastructure. Our team has been alerted.</p>
        <div className="mt-6 flex gap-3">
          <button onClick={() => nav("/app")} data-testid="prov-failed-dashboard" className="rounded-lg border border-slate-700 px-5 py-2.5 font-semibold text-slate-200 hover:border-slate-600">Go to dashboard</button>
          <button onClick={() => nav("/app/deploy")} data-testid="prov-failed-retry" className="rounded-lg bg-cyan-400 px-5 py-2.5 font-semibold text-[#06080D] hover:bg-cyan-300">Try again</button>
        </div>
      </Center>
    );
  }
  return (
    <Center wide>
      {phase === "done" ? (
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/40 bg-emerald-500/10"><PartyPopper className="h-8 w-8 text-emerald-400" /></div>
          <h1 className="mt-5 font-display text-3xl font-bold text-slate-50">Your offsite backup server is ready</h1>
          <p className="mt-2 text-slate-400">Everything's provisioned. Connect your Proxmox VE next.</p>
        </div>
      ) : (
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-slate-50">Provisioning your backup server</h1>
          <p className="mt-1 text-sm text-slate-400">This is fully automated — no action needed.</p>
        </div>
      )}

      {note && phase !== "done" && (
        <div data-testid="prov-note" className="mt-6 flex max-w-lg items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> {note}
        </div>
      )}

      <GlassCard className="mt-8 w-full max-w-lg p-6">
        <div className="space-y-1">
          {STEP_ORDER.map((key) => {
            const s = stepMap[key] || { status: "pending", label: key };
            return (
              <div key={key} className="flex items-center gap-3 rounded-lg px-3 py-2.5" data-testid={`prov-step-${key}`}>
                {s.status === "done" ? <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  : s.status === "running" ? <Loader2 className="h-5 w-5 animate-spin text-cyan-400" />
                  : <div className="h-5 w-5 rounded-full border-2 border-slate-700" />}
                <span className={`text-sm ${s.status === "pending" ? "text-slate-600" : s.status === "running" ? "text-cyan-300" : "text-slate-200"}`}>{s.label}</span>
                {s.status === "done" && <span className="ml-auto text-xs text-emerald-500">✓</span>}
              </div>
            );
          })}
        </div>
      </GlassCard>

      {phase === "done" && (
        <Link to={`/app/servers/${serverId}`} data-testid="prov-connect-cta" className="mt-8 inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-6 py-3 font-semibold text-[#06080D] hover:bg-cyan-300">
          Connect Proxmox VE <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </Center>
  );
}

function Center({ children, wide }) {
  return <div className="min-h-screen bg-[#06080D] flex flex-col items-center justify-center p-6"><div className={`flex flex-col items-center ${wide ? "w-full" : ""}`}>{children}</div></div>;
}
